import {
  apolloClient,
  CI,
  features,
  getContent,
  getGlobals,
  getLocale,
  getToken,
  handleApiError,
  logger,
  WPS_DONE_PAGE_QUERY,
} from '@/lib';
import type { GlobalsType } from '@/lib/globals';
import type { GenericObjectType } from '@/types';
import type { NavigateToReceiptPageCallback } from '@/lib/reactHooks/types';
import type { Maybe } from '@/generated/graphql';
import { APPLE_PAY_PAYSHEET_ERROR } from '@/components/Landing/constants';
import type {
  CreateSessionOptions,
  MerchantAddressConfig,
} from '@paypalcorp/checkout-apple-pay';
import { getErrorFromResponse } from '@/lib/apollo/utils';

const parseInitialData = (
  initialData:
    | GlobalsType['landingIWCData']
    | GlobalsType['initialGqlData']['checkoutSession'],
) => {
  // CheckoutSession typecheck
  if (
    initialData &&
    ('cart' in initialData ||
      'flags' in initialData ||
      'merchant' in initialData)
  ) {
    const { cart, flags, merchant } = initialData;
    return {
      cartData: cart,
      hideShipping: flags?.hideShipping,
      isChangeShippingAddressAllowed: flags?.isChangeShippingAddressAllowed,
      isSntSupported: flags?.isSntSupported,
      merchantCountry: merchant?.country,
      merchantName: merchant?.name,
    };
  }
  return initialData;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const merchantAddressConfigRequired = (cartData: any): boolean => {
  const cart = cartData;
  const merchantPassShippingAddress = cart?.shippingAddress;
  return Boolean(
    merchantPassShippingAddress?.city &&
      merchantPassShippingAddress?.state &&
      merchantPassShippingAddress?.postalCode,
  );
};

export const applePayPaymentRequest = (
  initialData:
    | GlobalsType['landingIWCData']
    | GlobalsType['initialGqlData']['checkoutSession'],
  navigateToReceiptPage: NavigateToReceiptPageCallback,
  merchantAddressConfig?: MerchantAddressConfig,
): CreateSessionOptions => {
  const { country: buyerCountry } = getLocale();
  const {
    cartData,
    hideShipping,
    merchantCountry,
    merchantName,
    isSntSupported,
  } = parseInitialData(initialData) ?? {};

  const merchantCapabilities = [
    'supports3DS',
    'supportsDebit',
    'supportsCredit',
  ];

  const shouldSupportNewNetworks = features.isActive(
    'applePayNewSupportedNetworks',
  );
  if (shouldSupportNewNetworks) {
    merchantCapabilities.push('supportsEMV');
    logger.info('IN_GUEST_APPLE_PAY_NEW_NETWORKS_TREATMENT');
  }

  const shouldUseShippingAndTaxId = features.isActive('isShippingAndTaxId');
  if (shouldUseShippingAndTaxId) {
    logger.info('IN_GUEST_APPLE_PAY_SHIPPING_AND_TAX_ID_TREATMENT');
  }

  const requiredShippingContactFields = ['email', 'phone'];
  if (!hideShipping) {
    requiredShippingContactFields.push('postalAddress', 'name');
  }

  // @ts-expect-error: ApplePayJS is only defined for users using Safari browser
  // through safari itself.
  const paymentRequest: ApplePayJS.ApplePayPaymentRequest = {
    // The merchant's two-letter ISO 3166 country code as a string.
    countryCode: merchantCountry as string,
    // The three-letter ISO 4217 currency code as a string
    currencyCode: cartData?.amounts?.total?.currencyCode as string,
    merchantCapabilities,
    requiredBillingContactFields: ['name', 'postalAddress', 'phone'],
    requiredShippingContactFields,
    supportedNetworks: [], // This gets overwritten by Checkout Apple Pay Module
    ...(!hideShipping && { shippingType: 'shipping' }),
    total: {
      amount: cartData?.amounts?.total?.currencyValue as string,
      label: `${merchantName} ${getContent('applepay.viapaypal')}`,
      type: 'final',
    },
  };

  const merchantDidPassAddress =
    merchantAddressConfig?.paymentRequest?.shippingContact;
  const merchantAddressConfigNeeded = merchantAddressConfigRequired(cartData);

  if (!merchantDidPassAddress && merchantAddressConfigNeeded) {
    // No merchant config data detected, despite it being required.
    // This could be because the user clicked the Apple Pay button
    // before merchant config hooked finished loading. However, they would have to
    // do that in less than a second during page load. Very Low chance as
    // merchant config hook runs in parallel with eligibiltiy.
    logger.error('NO_MERCHANT_CONFIG_FOUND_BUT_REQUIRED', {
      merchantAddressConfigNeeded,
      merchantDidPassAddress,
    });
  }

  const merchantShippingConfig = merchantAddressConfig?.merchantShippingConfig;

  if (merchantDidPassAddress) {
    paymentRequest.total = merchantAddressConfig?.paymentRequest?.total;
    if (!hideShipping) {
      //  show shipping address selector and shipping information in paysheet
      paymentRequest.lineItems =
        merchantAddressConfig?.paymentRequest?.lineItems;
      paymentRequest.shippingContact =
        merchantAddressConfig?.paymentRequest?.shippingContact;
      paymentRequest.shippingMethods =
        merchantAddressConfig?.paymentRequest?.shippingMethods;
    }
  }

  const onError = (appleError: any) => {
    if (
      appleError?.payload?.networkError ||
      appleError?.payload.graphQLErrors
    ) {
      if (!appleError.userCanRetry) {
        const error = getErrorFromResponse(appleError.payload);
        CI.fci();
        logger.flushThen(() => handleApiError(error));
      }
      logger.info('WEASLEY_APPLE_PAY_CAN_RETRY', appleError.userCanRetry);
    } else {
      CI.fci();
      // Shouldn't ever fall into this else case.
      logger.error('WEASLEY_APPLE_PAY_PAYLOAD_ERROR', appleError?.payload);
    }
  };
  // Apple Pay CI integration on Cancel Apple Pay Payment (Cancel button click)
  const onCancelApplePayPayment = () => CI.userCanceled();

  return {
    apiConfig: {
      apolloClient,
      cachePaymentApproval: {
        cacheQuery: WPS_DONE_PAGE_QUERY,
        variables: { token: getToken() },
      },
      token: getToken(),
    },
    eventHandlers: {
      onCancel: onCancelApplePayPayment,
      onError: onError,
      onSuccessfulPayment: navigateToReceiptPage,
    },
    logger: {
      ...logger,
      track: (payload: GenericObjectType) => {
        const eventName = payload.event_name || 'apple_pay_payment_sheet_event';
        if (payload.error_source) {
          //  error occurred during apple sheet flow, track for signalfx
          logger.metric(APPLE_PAY_PAYSHEET_ERROR, {
            dimensions: {
              error_source: payload.error_source,
            },
          });
        }
        logger.fpti(eventName, payload);
      },
    },
    loggerConfig: {
      trackingBuilder: () => ({ selected_funding_option: 'apple_pay' }),
    },
    merchantDomain: parseMerchantDomain(),
    ...(merchantDidPassAddress && !hideShipping && { merchantShippingConfig }),
    buyerCountry,
    hideShipping,
    isSntSupported,
    paymentRequest,
    shouldSupportNewNetworks,
    shouldUseShippingAndTaxId,
    version: 3,
  };
};

export const parseMerchantDomain = (): string => {
  try {
    const { uri } = getGlobals() || {};
    const base = uri?.base || 'https://www.paypal.com';
    const url = new URL(base);
    const merchantDomain = url.hostname;
    logger.info('APPLE_PAY_MERCHANT_DOMAIN_ON_HALF_SHEET', { merchantDomain });
    return merchantDomain;
  } catch (err) {
    logger.error('failed_to_parse_apple_pay_merchant_domain', { err });
    return 'www.paypal.com';
  }
};

export const getApplePayIneligibilityReason = (payload: {
  eligibilityResult?: { eligibility: boolean; reason: string };
  applePayButtonSeen?: Maybe<boolean>;
  isApplePayTreatment: boolean;
  isTreatmentOrControl?: boolean;
}): Array<string> => {
  const reasons: string[] = [];
  if (payload.eligibilityResult?.eligibility === false) {
    reasons.push(payload.eligibilityResult?.reason);
  }
  if (payload.applePayButtonSeen) {
    reasons.push('apple_pay_button_already_seen_on_previous_page');
  }
  if (payload.isApplePayTreatment === false) {
    reasons.push('user_not_in_apple_pay_treatment_group');
  }
  if (payload.isTreatmentOrControl === false) {
    reasons.push('user_not_in_treatment_group_and_not_in_control_group');
  }
  return reasons;
};
